import { useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useLazySubmitQuestionQuery } from '../Api/TestApi'
import { FormQuestion, useTest } from '../Providers/TestProvider'

export type SaveResult = { ok: true; confirmed: boolean } | { ok: false; error: unknown }

/**
 * The one place an answer is POSTed and confirmed. Both view modes and the
 * final submit use it, so the confirm rule lives once: `submitted` is raised
 * only after the server accepted the answer AND the stored answer is still the
 * one this request carried (a slow confirmation for an old answer must never
 * mark a newer, unsaved answer as saved).
 *
 * `.unwrap()` is load-bearing: an RTK lazy trigger resolves with `{error}` and
 * never rejects on its own. The confirm runs inside flushSync because an immer
 * producer is not guaranteed to run at dispatch time and the caller reads the
 * flag straight after. The returned promise never rejects; it is tracked in
 * the provider so a submit can wait for saves already in flight.
 */
const useSaveAnswer = () => {
	const { attempt, setFormQuestions, trackSave } = useTest()
	const [submitQuestion] = useLazySubmitQuestionQuery()

	return useCallback(
		(question: Pick<FormQuestion, 'id' | 'type'>, answer: any): Promise<SaveResult> =>
			trackSave(
				(async (): Promise<SaveResult> => {
					try {
						await submitQuestion({
							attempt_id: attempt.id,
							id: question.id,
							options: answer,
							question_id: question.id,
							type: question.type,
						}).unwrap()
					} catch (error) {
						// Leave `submitted` false and stay put: the final submit re-sends
						// every answered question with retries and will not finalize a
						// partial one. Never navigate the student out over a dropped packet.
						return { ok: false, error }
					}
					let confirmed = false
					flushSync(() => {
						setFormQuestions(draft => {
							const saved = draft.find(_ => _.id === question.id)
							if (saved && JSON.stringify(saved.answer) === JSON.stringify(answer)) {
								saved.submitted = true
								confirmed = true
							}
						})
					})
					return { ok: true, confirmed }
				})()
			),
		[attempt.id, setFormQuestions, submitQuestion, trackSave]
	)
}

export default useSaveAnswer
